
import { computed, defineComponent, onBeforeUnmount, ref, watch } from 'vue';

/** 试算监听字段：生意属性 + 各评估指标相关建档字段 */
const PREVIEW_FIELDS = [
  'businessProperty',
  'cooperationSystemCount',
  'deliBrandSlotCount',
  'flashCentralWarehouseProvinces',
  'supplyChainCentralWarehouseProvinces',
  'existingBusinessScale',
  'monthlyAverageOrders',
  'systemStoreCount',
  // 共用指标：人数
  'teamSize',
  'operationStaffCount',
  // 闪电仓指标：GMV
  'systemAnnualGMV',
  'learningOfficeGMV',
  'deliBrandGMV',
  // 供应链指标：多选选项 / 年销
  'cooperationSystemName',
  'mainBrand',
  'competitorAgentSituation',
  'learningOfficeAnnualSales',
  'customerMainBusiness'
];

const PREVIEW_DEBOUNCE_MS = 500;

export default defineComponent({
  name: 'EvaluationResultCard',
  props: {
    formData: {
      type: Object,
      default: () => ({})
    },
    /** 是否表单视图（创建/编辑）。true 时监听字段变更实时试算；详情页只读落库结果 */
    isFormView: {
      type: Boolean,
      default: false
    },
    /** widget 暴露的试算方法（@Widget.Method） */
    callEvaluatePreview: {
      type: Function,
      default: () => Promise.resolve(null)
    }
  },
  setup(props) {
    const result = ref<any>(null);
    let timer: ReturnType<typeof setTimeout> | null = null;
    let requestSeq = 0;

    const parseJson = (json: unknown): any => {
      if (!json || typeof json !== 'string') {
        return null;
      }
      try {
        return JSON.parse(json);
      } catch (e) {
        return null;
      }
    };

    /** 生意属性有值且结果非空才渲染 */
    const visible = computed(() => {
      const businessProperty = props.formData?.businessProperty;
      return !!businessProperty && !!result.value;
    });

    const starTagText = computed(() => {
      const text = result.value?.starLevelText;
      return text ? `${text}客户` : '';
    });

    const items = computed(() => {
      const list = result.value?.items;
      return Array.isArray(list) ? list : [];
    });

    const doPreview = async () => {
      const seq = ++requestSeq;
      try {
        const json = await props.callEvaluatePreview(props.formData || {});
        // 防并发：仅采用最后一次请求结果
        if (seq === requestSeq) {
          result.value = parseJson(json);
        }
      } catch (e) {
        if (seq === requestSeq) {
          result.value = null;
        }
      }
    };

    const schedulePreview = () => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        timer = null;
        doPreview();
      }, PREVIEW_DEBOUNCE_MS);
    };

    if (props.isFormView) {
      // 创建/编辑页：先展示落库回填值（编辑态），再监听指标字段变更实时试算
      result.value = parseJson(props.formData?.evaluationResultJson);
      watch(
        () => PREVIEW_FIELDS.map((name) => props.formData?.[name]),
        () => {
          schedulePreview();
        },
        { deep: true }
      );
    } else {
      // 详情页：只读后端从落库结果回填的字段值
      watch(
        () => props.formData?.evaluationResultJson,
        (json) => {
          result.value = parseJson(json);
        },
        { immediate: true }
      );
    }

    onBeforeUnmount(() => {
      if (timer) {
        clearTimeout(timer);
      }
    });

    return { visible, result, starTagText, items };
  }
});
